import { useState, useEffect } from 'react';

/**
 * Hook usePWA
 * Gère l'enregistrement du service worker et l'invite d'installation
 */
function usePWA() {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isInstallable, setIsInstallable] = useState(false);

  useEffect(() => {
    // Enregistrement du service worker
    if ('serviceWorker' in navigator) {
      // Un nouveau SW qui prend le contrôle (skipWaiting+clients.claim) signifie
      // qu'un déploiement plus récent est disponible : on recharge pour éviter
      // qu'un onglet reste bloqué sur un bundle JS obsolète (cause de l'écran noir).
      let refreshing = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (refreshing) return;
        refreshing = true;
        window.location.reload();
      });

      window.addEventListener('load', () => {
        navigator.serviceWorker
          .register('/sw.js')
          .then((reg) => {
            console.log('SW enregistré:', reg.scope);

            // Le navigateur ne vérifie une mise à jour du SW qu'à la navigation —
            // un onglet resté ouvert sans rechargement complet peut donc tourner
            // indéfiniment sur un bundle JS obsolète (incident du 23/10/2026 :
            // joueurs bloqués sur l'ancien code malgré un déploiement en prod).
            // On force une vérification active au retour au premier plan et
            // périodiquement tant que l'app reste ouverte.
            const checkForUpdate = () => reg.update().catch(() => {});
            document.addEventListener('visibilitychange', () => {
              if (document.visibilityState === 'visible') checkForUpdate();
            });
            setInterval(checkForUpdate, 5 * 60 * 1000);
          })
          .catch((err) => console.warn('SW échec:', err));
      });
    }

    // Détecte si l'app est déjà installée
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    // Capture l'invite d'installation native
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setIsInstallable(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // Détecte l'installation réussie
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setInstallPrompt(null);
    });

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const promptInstall = async () => {
    if (!installPrompt) return false;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') {
      setIsInstalled(true);
      setIsInstallable(false);
    }
    setInstallPrompt(null);
    return outcome === 'accepted';
  };

  return { isInstallable, isInstalled, promptInstall };
}

export default usePWA;
